import { z } from "zod";

/** Convierte un prefijo (`fescalona`) en `fescalona@viewmedonline.com`. */
export function normalizeLoginEmail(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (!trimmed.includes("@")) {
    return `${trimmed}@viewmedonline.com`;
  }
  return trimmed;
}

// Schema para login
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, "El correo electrónico es requerido")
    .transform(normalizeLoginEmail)
    .pipe(z.string().email("Formato de correo electrónico inválido")),
  password: z
    .string()
    .min(6, "La contraseña debe tener al menos 6 caracteres")
    .max(100, "La contraseña es demasiado larga"),
});

// Schema para registro
export const signupSchema = z
  .object({
    email: z
      .string()
      .min(1, "El correo electrónico es requerido")
      .email("Formato de correo electrónico inválido"),
    password: z
      .string()
      .min(6, "La contraseña debe tener al menos 6 caracteres")
      .max(100, "La contraseña es demasiado larga"),
    confirmPassword: z.string().min(1, "Confirma tu contraseña"),
    name: z
      .string()
      .min(1, "El nombre es requerido")
      .max(200, "El nombre es demasiado largo"),
    type: z
      .enum(["institution", "physician", "patient", "admin", "super_admin"])
      .default("patient"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

// Schema para cambio de contraseña
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "La contraseña actual es requerida"),
    newPassword: z
      .string()
      .min(6, "La nueva contraseña debe tener al menos 6 caracteres")
      .max(100, "La nueva contraseña es demasiado larga"),
    confirmNewPassword: z.string().min(1, "Confirma tu nueva contraseña"),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmNewPassword"],
  });

// Schema para reset de contraseña
export const resetPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "El correo electrónico es requerido")
    .email("Formato de correo electrónico inválido"),
});

// Schema para verificación de email
export const emailVerificationSchema = z.object({
  email: z
    .string()
    .min(1, "El correo electrónico es requerido")
    .email("Formato de correo electrónico inválido"),
});

// Schema para datos de autenticación de Firebase
export const firebaseAuthSchema = z.object({
  uid: z.string(),
  email: z.string().email().nullable(),
  emailVerified: z.boolean(),
  displayName: z.string().nullable(),
  photoURL: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  providerData: z.array(
    z.object({
      uid: z.string().optional(),
      displayName: z.string().nullable(),
      email: z.string().nullable(),
      phoneNumber: z.string().nullable(),
      photoURL: z.string().nullable(),
      providerId: z.string(),
    })
  ),
  metadata: z.object({
    lastSignInTime: z.string().nullable(),
    creationTime: z.string().nullable(),
  }),
});

// Schema para sesión de usuario
export const userSessionSchema = z.object({
  user: firebaseAuthSchema,
  token: z.string().optional(),
  expiresAt: z.number().optional(),
});

// Tipos inferidos de los schemas
export type LoginFormData = z.infer<typeof loginSchema>;
export type SignupFormData = z.infer<typeof signupSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type EmailVerificationFormData = z.infer<typeof emailVerificationSchema>;
export type FirebaseAuth = z.infer<typeof firebaseAuthSchema>;
export type UserSession = z.infer<typeof userSessionSchema>;
